import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; error?: { code?: string }; data?: unknown }
  if (!b?.success) {
    const err = new Error(sanitizeBusinessMessage(b?.message, 'Request failed')) as Error & { code?: string }
    err.code = b.error?.code
    throw err
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type StorefrontHeroSlide = {
  id: string
  image: string
  title: string
  subtitle: string
  ctaText: string
  badge: string | null
  ctaHref?: string | null
}

export type StorefrontTrustBadge = {
  icon: string
  label: string
  sublabel: string
}

export type StorefrontContentPayload = {
  heroSlides: StorefrontHeroSlide[]
  trustBadges: StorefrontTrustBadge[]
}

export type StoreOrganisationSummary = {
  id: number
  name: string
  status?: string
  currency?: string | null
}

/** Mirrors API `STORE_PROFILE_LIMITS` in shopora_Api `store.dto.ts`. */
export const STORE_PROFILE_LIMITS = {
  aboutText: 8000,
  tagline: 255,
  sellingSummary: 5000,
  contactAddress: 512,
  businessHours: 512,
} as const

export type StoreSocialLinks = {
  instagram?: string | null
  facebook?: string | null
  x?: string | null
  website?: string | null
  whatsapp?: string | null
}

export type StoreRecord = {
  id: number
  organisationId: number
  name: string
  subdomain: string
  organisation?: StoreOrganisationSummary
  logo?: string | null
  /** Square browser-tab icon (validated PNG); empty/null = Kartly's default icon. */
  faviconUrl?: string | null
  /** Set when the platform team has suspended this store; shoppers see "temporarily unavailable". */
  suspendedAt?: string | null
  /** Why it was suspended (written by the platform team for the store owner). */
  suspensionReason?: string | null
  /** Wide image for the customer `/about` page only. */
  aboutBannerUrl?: string | null
  primaryColor?: string | null
  secondaryColor?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  /** What this shop sells / niche (AI + profile). */
  sellingSummary?: string | null
  aboutText?: string | null
  tagline?: string | null
  contactAddress?: string | null
  businessHours?: string | null
  /** Resolved ISO 4217 from API (store override or organisation default) */
  currency?: string | null
  /** Default storefront/email language and the languages the shop offers (en, es, de, fr) */
  defaultLocale?: string
  supportedLocales?: string[]
  /** Sales tax rate 0–100 for checkout */
  taxRatePercent?: number
  /** Alert when product stock is at or below this quantity */
  lowStockThreshold?: number
  onlinePaymentsEnabled?: boolean
  onlinePaymentsAvailable?: boolean
  payOnDeliveryEnabled?: boolean
  fulfillmentDeliveryEnabled?: boolean
  fulfillmentPickupEnabled?: boolean
  /** In-store POS; default false; enabled by platform admin */
  posEnabled?: boolean
  socialLinks?: StoreSocialLinks | null
  storefrontContent?: StorefrontContentPayload | null
  /** Set when org admin dismisses the dashboard onboarding checklist (all steps complete). */
  onboardingChecklistDismissedAt?: string | null
  /** Set when org admin opens the customer storefront from the checklist preview action. */
  onboardingStorefrontPreviewedAt?: string | null
}

export async function listStores(): Promise<{ items: StoreRecord[] }> {
  try {
    const { data } = await api.get('/stores')
    return assertSuccess(data) as { items: StoreRecord[] }
  } catch (e) {
    throw apiError(e, 'Failed to load stores')
  }
}

export async function updateStore(
  storeId: number,
  payload: Partial<Omit<StoreRecord, 'currency'>> & { currency?: string | null },
): Promise<StoreRecord> {
  try {
    const { data } = await api.patch(`/stores/${encodeURIComponent(storeId)}`, payload)
    return assertSuccess(data) as StoreRecord
  } catch (e) {
    throw apiError(e, 'Failed to update store')
  }
}

export type CreateStoreWithPlanPayload = {
  planCode: 'standard' | 'premium' | 'plus'
  billingInterval: 'monthly' | 'yearly'
  store: {
    name: string
    subdomain: string
    contactEmail?: string
    contactPhone?: string
    sellingSummary?: string
  }
}

/** POST /storebusiness/stores — additional store for store_owner accounts. */
export async function createStoreWithPlan(payload: CreateStoreWithPlanPayload): Promise<StoreRecord> {
  try {
    const { data } = await api.post('/storebusiness/stores', payload)
    return assertSuccess(data) as StoreRecord
  } catch (e) {
    throw apiError(e, 'Failed to create store')
  }
}
